import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import FloatingAdminMenu from "./FloatMenu";
import Tooltip from "./Tooltip";

import {
  FiEye, FiEyeOff, FiLock, FiEdit, FiTrash,
  FiPlus, FiX, FiCalendar, FiMapPin, FiLogOut,
  FiGlobe, FiAlertCircle, FiCheck, FiLoader,
} from "react-icons/fi";

import fondo from "../assets/fondo1.jpg";

const API      = "https://aldanaselena.com/viajes.php";
const PASSWORD = "aldana.admin";

const initialForm = { mes: "", ciudad: "", pais: "", fecha_inicio: "", fecha_fin: "" };

/* ─── Paleta ─── */
const C = {
  gold:      "#C9A96E",
  goldDim:   "#8B6E47",
  goldSub:   "rgba(201,169,110,0.12)",
  bg:        "#0A0806",
  card:      "#110D0A",
  cardHov:   "#17120E",
  border:    "rgba(201,169,110,0.12)",
  borderHov: "rgba(201,169,110,0.35)",
  text:      "#EDE0D0",
  textDim:   "rgba(237,224,208,0.45)",
  textFaint: "rgba(237,224,208,0.22)",
};

/* ─── Variants ─── */
const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.25,0.1,0.25,1] } },
};

const stagger = {
  hidden: {},
  show:   { transition: { staggerChildren: 0.07 } },
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.94 },
  show:   { opacity: 1, scale: 1, transition: { duration: 0.45, ease: [0.25,0.1,0.25,1] } },
};

/* ─── Helpers ─── */
const GoldLine = () => (
  <div className="h-px my-8"
       style={{ background: `linear-gradient(90deg, transparent, ${C.gold}40, transparent)` }} />
);

const SectionLabel = ({ children }) => (
  <p className="text-[10px] tracking-[0.4em] uppercase font-light"
     style={{ color: C.goldDim }}>{children}</p>
);

/* ─── Modal Input ─── */
const ModalInput = ({ name, value, onChange, placeholder, icon, type = "text" }) => {
  const [focused, setFocused] = useState(false);
  return (
    <motion.div
      animate={{ borderColor: focused ? C.gold : C.border }}
      transition={{ duration: 0.25 }}
      className="flex items-center gap-3 rounded-xl px-4 py-3"
      style={{ background: C.bg, border: `1px solid ${C.border}` }}
    >
      <span style={{ color: focused ? C.gold : C.goldDim }} className="flex-shrink-0 transition-colors duration-300">
        {icon}
      </span>
      <input
        type={type} name={name} value={value} onChange={onChange}
        placeholder={placeholder}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        className="w-full bg-transparent text-sm font-light outline-none placeholder:text-[#EDE0D0]/20"
        style={{ color: C.text }}
      />
    </motion.div>
  );
};

/* ─── Tarjeta de viaje ─── */
const ViajeCard = ({ v, onEdit, onDelete, delay = 0 }) => {
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <motion.div
      variants={scaleIn}
      transition={{ delay }}
      layout
      className="rounded-2xl p-6 group relative overflow-hidden"
      style={{ background: C.card, border: `1px solid ${C.border}` }}
      whileHover={{ borderColor: C.borderHov, background: C.cardHov,
                    transition: { duration: 0.3 } }}
    >
      {/* Acento dorado izquierdo */}
      <motion.div
        initial={{ scaleY: 0 }} whileHover={{ scaleY: 1 }}
        transition={{ duration: 0.3 }}
        className="absolute left-0 top-4 bottom-4 w-[2px] rounded-full origin-top"
        style={{ background: C.gold }}
      />

      {/* Contenido */}
      <div className="pl-2">
        <SectionLabel>{v.mes}</SectionLabel>

        <div className="flex items-start gap-2 mt-2 mb-1">
          <FiMapPin size={13} className="mt-0.5 flex-shrink-0" style={{ color: C.gold }} />
          <h3 className="font-light text-lg leading-tight" style={{ color: C.text }}>
            {v.ciudad}
          </h3>
        </div>

        <div className="flex items-center gap-2 mb-4">
          <FiGlobe size={11} style={{ color: C.goldDim }} />
          <span className="text-xs font-light tracking-wide" style={{ color: C.textDim }}>
            {v.pais}
          </span>
        </div>

        {/* Fechas */}
        <div className="flex items-center gap-2 text-[11px] font-light"
             style={{ color: C.textFaint }}>
          <FiCalendar size={11} style={{ color: C.goldDim }} />
          <span>
            {v.fecha_inicio}
            <span className="mx-2" style={{ color: C.border }}>→</span>
            {v.fecha_fin}
          </span>
        </div>

        <GoldLine />

        {/* Acciones */}
        <div className="flex items-center gap-3">
          {/* Editar */}
          <Tooltip text="Editar viaje">
            <motion.button
              whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.94 }}
              onClick={() => onEdit(v)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-light tracking-wider uppercase transition-all"
              style={{ border: `1px solid ${C.border}`, color: C.goldDim }}
            >
              <FiEdit size={12} />
              Editar
            </motion.button>
          </Tooltip>

          {/* Eliminar */}
          <AnimatePresence mode="wait">
            {confirmDelete ? (
              <motion.div
                key="confirm"
                initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}
                className="flex items-center gap-2"
              >
                <span className="text-[10px] tracking-widest uppercase"
                      style={{ color: "rgba(224,122,122,0.7)" }}>
                  ¿Confirmar?
                </span>
                <motion.button
                  whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.94 }}
                  onClick={() => onDelete(v.id)}
                  className="flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-light uppercase transition-all"
                  style={{ border: "1px solid rgba(224,122,122,0.3)", color: "#E07A7A" }}
                >
                  <FiCheck size={11} />
                  Sí
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.94 }}
                  onClick={() => setConfirmDelete(false)}
                  className="p-2 rounded-xl text-xs transition-all"
                  style={{ border: `1px solid ${C.border}`, color: C.textDim }}
                >
                  <FiX size={11} />
                </motion.button>
              </motion.div>
            ) : (
              <motion.button
                key="delete"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.94 }}
                onClick={() => setConfirmDelete(true)}
                className="w-9 h-9 flex items-center justify-center rounded-xl transition-all"
                style={{ border: "1px solid rgba(224,122,122,0.2)", color: "#E07A7A" }}
              >
                <FiTrash size={13} />
              </motion.button>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

/* ══════════════════════════════════════
   COMPONENTE PRINCIPAL
══════════════════════════════════════ */
const AdminViajes = () => {
  const [viajes,        setViajes]        = useState([]);
  const [form,          setForm]          = useState(initialForm);
  const [editingId,     setEditingId]     = useState(null);
  const [showModal,     setShowModal]     = useState(false);
  const [showMenu,      setShowMenu]      = useState(false);
  const [isAuth,        setIsAuth]        = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [showPassword,  setShowPassword]  = useState(false);
  const [errorAuth,     setErrorAuth]     = useState("");
  const [loading,       setLoading]       = useState(false);
  const [submitting,    setSubmitting]    = useState(false);
  const [submitState,   setSubmitState]   = useState("idle"); // idle | success | error

  /* ── Auth ── */
  useEffect(() => {
    if (localStorage.getItem("admin_auth") === "true") setIsAuth(true);
  }, []);

  const handleLogin = () => {
    if (passwordInput === PASSWORD) {
      setIsAuth(true);
      localStorage.setItem("admin_auth", "true");
      setErrorAuth("");
    } else {
      setErrorAuth("Contraseña incorrecta");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_auth");
    setIsAuth(false);
  };

  /* ── CRUD ── */
  const fetchViajes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API);
      setViajes(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { if (isAuth) fetchViajes(); }, [isAuth]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const openCreate = () => {
    setForm(initialForm);
    setEditingId(null);
    setSubmitState("idle");
    setShowModal(true);
  };

  const handleEdit = (v) => {
    setForm(v);
    setEditingId(v.id);
    setSubmitState("idle");
    setShowModal(true);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      if (editingId) {
        await axios.put(`${API}?id=${editingId}`, form);
      } else {
        await axios.post(API, form);
      }
      setSubmitState("success");
      await fetchViajes();
      setTimeout(() => { setShowModal(false); setSubmitState("idle"); }, 900);
    } catch {
      setSubmitState("error");
      setTimeout(() => setSubmitState("idle"), 2000);
    }
    setSubmitting(false);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}?id=${id}`);
      setViajes((prev) => prev.filter((v) => v.id !== id));
    } catch (err) { console.error(err); }
  };

  const closeModal = () => { setShowModal(false); setSubmitState("idle"); };

  return (
    <div className="relative min-h-screen overflow-hidden" style={{ background: C.bg }}>

      {/* Fondo */}
      <img src={fondo} alt="" aria-hidden
           className="absolute inset-0 w-full h-full object-cover opacity-[0.06]" />
      <div className="absolute inset-0"
           style={{ background: "radial-gradient(ellipse at 70% 0%, rgba(201,169,110,0.05) 0%, transparent 60%)" }} />

      {/* Línea dorada top */}
      <div className="fixed top-0 left-0 right-0 h-[1px] z-50"
           style={{ background: `linear-gradient(90deg, transparent, ${C.gold}50, transparent)` }} />

      {/* ════════════ LOGIN ════════════ */}
      <AnimatePresence>
        {!isAuth && (
          <motion.div
            className="fixed inset-0 z-[99999] flex items-center justify-center px-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          >
            <motion.div className="absolute inset-0 backdrop-blur-sm"
                         style={{ background: "rgba(10,8,6,0.92)" }} />

            <motion.div
              initial={{ scale: 0.9, y: 24, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 24, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.25,0.1,0.25,1] }}
              className="relative z-10 w-full max-w-sm rounded-2xl p-8 shadow-2xl"
              style={{ background: C.card, border: `1px solid ${C.border}` }}
            >
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                     style={{ background: C.goldSub, color: C.gold }}>
                  <FiLock size={16} />
                </div>
                <div>
                  <SectionLabel>Acceso restringido</SectionLabel>
                  <h2 className="font-light tracking-[0.2em] text-sm mt-0.5" style={{ color: C.text }}>
                    PANEL DE VIAJES
                  </h2>
                </div>
              </div>

              <div className="relative mb-4">
                <input
                  type={showPassword ? "text" : "password"}
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                  placeholder="Contraseña"
                  className="w-full rounded-xl px-4 py-3 pr-12 text-sm font-light outline-none placeholder:text-[#EDE0D0]/20"
                  style={{ background: C.bg, color: C.text, border: `1px solid ${C.border}` }}
                />
                <button
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 transition-colors"
                  style={{ color: C.goldDim }}
                >
                  {showPassword ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                </button>
              </div>

              <AnimatePresence>
                {errorAuth && (
                  <motion.p
                    initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    className="flex items-center gap-2 text-xs mb-4"
                    style={{ color: "#E07A7A" }}
                  >
                    <FiAlertCircle size={12} /> {errorAuth}
                  </motion.p>
                )}
              </AnimatePresence>

              <motion.button
                whileHover={{ opacity: 0.88 }} whileTap={{ scale: 0.97 }}
                onClick={handleLogin}
                className="w-full py-3 rounded-xl text-sm font-light tracking-[0.25em] uppercase"
                style={{ background: C.gold, color: "#0A0806" }}
              >
                Ingresar
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Menú flotante */}
      {isAuth && <FloatingAdminMenu showMenu={showMenu} setShowMenu={setShowMenu} />}

      {/* ════════════ PANEL ════════════ */}
      {isAuth && (
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 pt-28 pb-24">

          {/* Header */}
          <motion.div
            initial="hidden" animate="show" variants={stagger}
            className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-12"
          >
            <motion.div variants={fadeUp}>
              <SectionLabel>Panel de administración</SectionLabel>
              <h1 className="font-light tracking-[0.25em] text-3xl md:text-5xl mt-1"
                  style={{ color: C.gold }}>
                VIAJES
              </h1>
              <p className="text-xs font-light mt-3" style={{ color: C.textFaint }}>
                {viajes.length} {viajes.length === 1 ? "destino registrado" : "destinos registrados"}
              </p>
            </motion.div>

            <motion.div variants={fadeUp} className="flex items-center gap-3">
              <motion.button
                whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                onClick={openCreate}
                className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-light tracking-[0.2em] uppercase"
                style={{ background: C.gold, color: "#0A0806" }}
              >
                <FiPlus size={14} />
                Nuevo viaje
              </motion.button>

              <Tooltip text="Cerrar sesión">
                <motion.button
                  whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                  onClick={handleLogout}
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ border: "1px solid rgba(224,122,122,0.2)", color: "#E07A7A" }}
                >
                  <FiLogOut size={15} />
                </motion.button>
              </Tooltip>
            </motion.div>
          </motion.div>

          <GoldLine />

          {/* Loading */}
          <AnimatePresence>
            {loading && (
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="flex flex-col items-center justify-center py-32 gap-4"
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                  className="w-10 h-10 rounded-full"
                  style={{ border: `1px solid ${C.border}`, borderTopColor: C.gold }}
                />
                <p className="text-[10px] tracking-[0.4em] uppercase" style={{ color: C.goldDim }}>
                  Cargando destinos
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Empty state */}
          {!loading && viajes.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center justify-center py-32 text-center gap-4"
            >
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
                   style={{ background: C.goldSub, color: C.goldDim }}>
                <FiGlobe size={22} />
              </div>
              <p className="text-xs tracking-[0.3em] uppercase font-light" style={{ color: C.textDim }}>
                Sin destinos cargados
              </p>
              <motion.button
                whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}
                onClick={openCreate}
                className="mt-2 px-8 py-3 rounded-xl text-xs font-light tracking-[0.25em] uppercase"
                style={{ background: C.goldSub, color: C.gold, border: `1px solid ${C.border}` }}
              >
                Agregar primer destino
              </motion.button>
            </motion.div>
          )}

          {/* Grid de viajes */}
          {!loading && viajes.length > 0 && (
            <LayoutGroup>
              <motion.div
                variants={stagger} initial="hidden" animate="show"
                className="grid md:grid-cols-2 xl:grid-cols-3 gap-5"
              >
                {viajes.map((v, i) => (
                  <ViajeCard
                    key={v.id}
                    v={v}
                    delay={i * 0.05}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                ))}
              </motion.div>
            </LayoutGroup>
          )}
        </div>
      )}

      {/* ════════════ MODAL ════════════ */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            className="fixed inset-0 z-[99999] flex items-center justify-center px-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          >
            {/* Overlay */}
            <motion.div
              className="absolute inset-0 backdrop-blur-md cursor-pointer"
              style={{ background: "rgba(10,8,6,0.88)" }}
              onClick={closeModal}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            />

            {/* Modal card */}
            <motion.div
              initial={{ scale: 0.92, y: 32, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.92, y: 32, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.25,0.1,0.25,1] }}
              className="relative w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-2xl p-7 shadow-2xl"
              style={{ background: C.card, border: `1px solid ${C.border}` }}
            >
              {/* Línea dorada sup */}
              <div className="absolute top-0 left-8 right-8 h-[1px]"
                   style={{ background: `linear-gradient(90deg, transparent, ${C.gold}40, transparent)` }} />

              {/* Header modal */}
              <div className="flex items-start justify-between mb-8">
                <div>
                  <SectionLabel>{editingId ? "Editar destino" : "Nuevo destino"}</SectionLabel>
                  <h2 className="font-light text-xl tracking-wide mt-1" style={{ color: C.text }}>
                    {editingId ? form.ciudad || "Editar viaje" : "Agregar viaje"}
                  </h2>
                </div>

                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }} whileTap={{ scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                  onClick={closeModal}
                  className="w-9 h-9 rounded-xl flex items-center justify-center mt-1"
                  style={{ border: `1px solid ${C.border}`, color: C.textDim }}
                >
                  <FiX size={15} />
                </motion.button>
              </div>

              {/* Campos */}
              <div className="space-y-5">

                {/* Mes */}
                <div>
                  <SectionLabel>Mes</SectionLabel>
                  <div className="mt-2">
                    <ModalInput
                      name="mes" value={form.mes} onChange={handleChange}
                      placeholder="Ej: Junio 2025"
                      icon={<FiCalendar size={13} />}
                    />
                  </div>
                </div>

                {/* Ciudad + País */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <SectionLabel>Ciudad</SectionLabel>
                    <div className="mt-2">
                      <ModalInput
                        name="ciudad" value={form.ciudad} onChange={handleChange}
                        placeholder="Buenos Aires"
                        icon={<FiMapPin size={13} />}
                      />
                    </div>
                  </div>
                  <div>
                    <SectionLabel>País</SectionLabel>
                    <div className="mt-2">
                      <ModalInput
                        name="pais" value={form.pais} onChange={handleChange}
                        placeholder="Argentina"
                        icon={<FiGlobe size={13} />}
                      />
                    </div>
                  </div>
                </div>

                {/* Fechas */}
                <div>
                  <SectionLabel>Período</SectionLabel>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div>
                      <p className="text-[10px] font-light mb-1.5" style={{ color: C.textFaint }}>Inicio</p>
                      <ModalInput
                        name="fecha_inicio" value={form.fecha_inicio}
                        onChange={handleChange} type="date"
                        icon={<FiCalendar size={12} />}
                        placeholder=""
                      />
                    </div>
                    <div>
                      <p className="text-[10px] font-light mb-1.5" style={{ color: C.textFaint }}>Fin</p>
                      <ModalInput
                        name="fecha_fin" value={form.fecha_fin}
                        onChange={handleChange} type="date"
                        icon={<FiCalendar size={12} />}
                        placeholder=""
                      />
                    </div>
                  </div>
                </div>
              </div>

              <GoldLine />

              {/* Botones modal */}
              <div className="flex items-center justify-between gap-4">
                <motion.button
                  whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                  onClick={closeModal}
                  className="flex-1 py-3 rounded-xl text-xs font-light tracking-[0.25em] uppercase"
                  style={{ border: `1px solid ${C.border}`, color: C.textDim }}
                >
                  Cancelar
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.02, opacity: 0.9 }} whileTap={{ scale: 0.97 }}
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex-1 py-3 rounded-xl text-xs font-light tracking-[0.25em] uppercase flex items-center justify-center gap-2 transition-all"
                  style={{
                    background: submitState === "success" ? "#3D6B4F"
                               : submitState === "error"   ? "#7B3B3B"
                               : C.gold,
                    color: submitState === "idle" ? "#0A0806" : C.text,
                  }}
                  animate={{ background: submitState === "success" ? "#3D6B4F"
                                        : submitState === "error"   ? "#7B3B3B"
                                        : C.gold }}
                  transition={{ duration: 0.3 }}
                >
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={submitState}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      className="flex items-center gap-2"
                    >
                      {submitState === "saving"  && <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease:"linear" }}><FiLoader size={13}/></motion.div>}
                      {submitState === "success" && <FiCheck size={13} />}
                      {submitState === "error"   && <FiAlertCircle size={13} />}
                      {submitState === "idle"    && <FiPlus size={13} />}

                      {submitState === "idle"    && (editingId ? "Guardar cambios" : "Crear viaje")}
                      {submitState === "saving"  && "Guardando..."}
                      {submitState === "success" && "¡Guardado!"}
                      {submitState === "error"   && "Error al guardar"}
                    </motion.span>
                  </AnimatePresence>
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminViajes;